import { Paper } from '@material-ui/core'
import { GoogleApiWrapper } from 'google-maps-react'
import ErrorBoundary from 'app/components/ErrorBoundary'
import apiConfigs from 'app/configs/apiConfigs'
import { RegisterSuggestionList } from 'app/componentV4/RegisterSuggestionList'
import PropTypes from 'prop-types'
import { isNil, head, prop, mergeRight } from 'ramda'
import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Script from 'react-load-script'
import PlacesAutocomplete, {
  geocodeByPlaceId,
  getLatLng,
} from 'react-places-autocomplete'
import tzlookup from 'tz-lookup'
import { useField } from 'formik'
import {
  TextField,
  InputAdornment,
  Button,
  Typography,
} from '@material-ui/core'
import { displayCountLimit } from 'commons/number'
import clsx from 'clsx'
import { ErrorIcon } from 'app/components/icons'

const BasePlacesAutocompleteFormik = ({
  classes,
  defaultCountry,
  inputProps,
  InputProps,
  labelId,
  parseValue,
  placeholderId,
  required,
  clearBtn,
  variant = false,
  selectSuggestion = false,
  clickedSuggestion = false,
  isShowMaxLength = true,
  restriction = false,
  isMapLoaded = null,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const [field, meta, helpers] = useField(props.name)
  const value = field.value
  const { setValue, setTouched } = helpers
  const isError = !isNil(meta.error) && meta.touched

  const [isLoadingExternalScript, setIsLoadingExternalScript] = useState(
    isMapLoaded === null ? true : !isMapLoaded,
  )
  useEffect(() => {
    if (isMapLoaded !== null) {
      setIsLoadingExternalScript(!isMapLoaded)
    }
  }, [isMapLoaded])

  const handleSelectPlaceSuggestion = (name) => async () => {
    const result = head(await geocodeByPlaceId(prop('placeId', name)))

    const latLng = await getLatLng(result)
    const timeZoneId = tzlookup(prop('lat', latLng), prop('lng', latLng))
    setValue({
      suggestion: parseValue(mergeRight(name, result)),
      ...latLng,
      timeZoneId,
    })
    setTouched(true)
  }

  const onTextClear = () => {
    setTouched(true)
    setValue({
      suggestion: '',
      lat: '',
      lng: '',
      timeZoneId: '',
    })
  }

  const searchOptions = {}
  if (restriction) {
    searchOptions.types = ['address']
    searchOptions.componentRestrictions = { country: restriction }
  }

  const handleChange = (event) => {
    setTouched(true)
    setValue({ suggestion: event, lat: '', lng: '', timeZoneId: '' })
  }
  return (
    <ErrorBoundary
      fallback={
        <div className="flex flex-col p-8 rounded shadow-lg">
          <span className="font-bold">
            <FormattedMessage id="error.general" />
          </span>
        </div>
      }
    >
      {!isLoadingExternalScript && (
        <div className="w-full">
          <PlacesAutocomplete
            {...field}
            value={value.suggestion}
            onChange={handleChange}
            onError={() => {}}
            searchOptions={searchOptions}
          >
            {({
              getInputProps,
              suggestions,
              getSuggestionItemProps,
              loading,
            }) => {
              return (
                <div className={clsx('relative', classes.noBorder)}>
                  <TextField
                    className={clsx(
                      'w-full bg-white rounded-8 border border-grey-input',
                      isError
                        ? classes.placeAddressError
                        : classes.placeAddress,
                    )}
                    {...getInputProps()}
                    placeholder={formatMessage({ id: placeholderId })}
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <div className="text-center">
                            <div>
                              <Button
                                variant="text"
                                className="w-10 p-0 min-w-0 opacity-50 text-gray-500 hover:bg-white"
                                onClick={onTextClear}
                              >
                                <svg
                                  width="25px"
                                  height="25px"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <g id="Edit / Close_Circle">
                                    <path
                                      id="Vector"
                                      d="M9 9L11.9999 11.9999M11.9999 11.9999L14.9999 14.9999M11.9999 11.9999L9 14.9999M11.9999 11.9999L14.9999 9M12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21Z"
                                      stroke="#a8a8a8"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </g>
                                </svg>
                              </Button>
                            </div>
                            <div>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {displayCountLimit(
                                  value.suggestion.length,
                                  190,
                                )}
                              </Typography>
                            </div>
                          </div>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <div className="relative">
                    <Paper
                      className="absolute b-0 l-0 w-full"
                      style={{ zIndex: 3 }}
                    >
                      <RegisterSuggestionList
                        suggestions={suggestions}
                        getSuggestionItemProps={getSuggestionItemProps}
                        onSelectPlaceSuggestion={handleSelectPlaceSuggestion}
                        clickedSuggestion={clickedSuggestion}
                      />
                    </Paper>
                  </div>
                </div>
              )
            }}
          </PlacesAutocomplete>
          {isError && (
            <div className="flex space-x-2 py-1">
              <div className="flex my-auto">
                <ErrorIcon classes="w-14" />
              </div>
              <label className="text-red">{meta.error}</label>
            </div>
          )}
        </div>
      )}
    </ErrorBoundary>
  )
}

BasePlacesAutocompleteFormik.propTypes = {
  labelId: PropTypes.string.isRequired,
  placeholderId: PropTypes.string.isRequired,
  inputProps: PropTypes.object,
  InputProps: PropTypes.object,
  parseValue: PropTypes.func.isRequired,
}

export default GoogleApiWrapper({
  apiKey: apiConfigs.googleAPI,
})(BasePlacesAutocompleteFormik)
